import { useState, useRef, useEffect, useMemo } from "react";

export interface SimpleSelectProps {
  label?: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  hideLabel?: boolean;
}

export default function SimpleSelect({
  label,
  value,
  options,
  onChange,
  placeholder = "Select...",
  required = false,
  disabled = false,
  hideLabel = false,
}: SimpleSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch("");
      }
    };
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  useEffect(() => {
    if (open) setSearch("");
  }, [open]);

  const filtered = useMemo(() => {
    if (!search.trim()) return options;
    const q = search.toLowerCase();
    return options.filter((o) => o.toLowerCase().includes(q));
  }, [options, search]);

  return (
    <div ref={ref} className="relative">
      {!hideLabel && label && (
        <label className="text-xs font-medium text-gray-600 flex items-center gap-1 mb-1.5">
          {label}
          {required && <span className="text-red-500">*</span>}
        </label>
      )}
      <button
        type="button"
        onClick={() => !disabled && setOpen((v) => !v)}
        disabled={disabled}
        className={`w-full px-3 py-2 text-sm border rounded-lg flex items-center justify-between transition-colors ${
          disabled
            ? "bg-gray-50 border-gray-100 text-gray-300 cursor-not-allowed"
            : open
              ? "border-[#830051] bg-white cursor-pointer"
              : "border-gray-200 bg-white hover:border-gray-300 cursor-pointer"
        }`}
      >
        <span className={value ? "text-gray-800" : "text-gray-400 truncate"}>
          {value || placeholder}
        </span>
        <i
          className={`ri-arrow-down-s-line text-gray-400 transition-transform ${open ? "rotate-180" : ""}`}
        ></i>
      </button>

      {open && (
        <div
          className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg z-20 overflow-hidden"
          style={{ boxShadow: "0 4px 16px rgba(0,0,0,0.08)" }}
        >
          {/* Search input */}
          <div className="px-2 pt-2 pb-1">
            <div className="flex items-center gap-1.5 px-2 py-1.5 border border-gray-200 rounded-md bg-white">
              <i className="ri-search-line text-gray-400 text-xs"></i>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search..."
                className="flex-1 text-sm outline-none bg-transparent text-gray-700 placeholder-gray-400 min-w-0"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    setOpen(false);
                    setSearch("");
                  }
                }}
              />
              {search && (
                <button onClick={() => setSearch("")} className="shrink-0 cursor-pointer">
                  <i className="ri-close-line text-gray-400 text-xs"></i>
                </button>
              )}
            </div>
          </div>

          {/* Options list */}
          <div className="max-h-44 overflow-y-auto">
            {filtered.length === 0 && (
              <div className="px-3 py-2.5 text-xs text-gray-400">No options available</div>
            )}
            {filtered.map((opt) => (
              <button
                key={opt}
                type="button"
                onClick={() => {
                  onChange(opt);
                  setOpen(false);
                  setSearch("");
                }}
                className={`w-full px-3 py-2 text-sm text-left cursor-pointer hover:bg-pink-50 transition-colors ${
                  opt === value ? "text-[#830051] font-semibold bg-pink-50" : "text-gray-700"
                }`}
              >
                {opt}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
